"use client";

import { ArrowRightIcon, BadgeCheck, Brain } from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Bot, UserCog, BarChart2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {THome_Config } from "@/app/config";
import Link from "next/link";
import { motion } from "motion/react";

const featureItems = [
  {
    icon: BookOpen,
  },
  {
    icon: Bot,
  },
  {
    icon: UserCog,
  },
  {
    icon: BarChart2,
  },
];

const sideCards = [
  {
    icon: BookOpen,
  },
  {
    icon: Bot,
  },
  {
    icon: BadgeCheck,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2
    }
  }
};



export const EducationSection = ({config}:{config:THome_Config['education']})=> {
  return (
    <motion.section
      className="w-full py-12 md:py-[84px] bg-white flex justify-center"
      id="education"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="flex flex-col max-w-[1120px] items-start gap-8 md:gap-14 px-4 md:px-[21px] py-0 w-full">
        <motion.header 
          className="flex flex-col items-center gap-3 md:gap-3.5 pt-[1.25px] pb-0 px-0 w-full"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.7,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
          >
            <Badge className="inline-flex items-center justify-center px-2 py-[2.75px] bg-white rounded-[6.75px] border border-solid border-[#bddaff]">
              <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#155dfc] text-[10.5px] text-center tracking-[0] leading-[14px]">
               {config.tag}
              </span>
            </Badge>
          </motion.div>

          <motion.div 
            className="flex flex-col items-center w-full"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7,
                  ease: "easeOut"
                }
              }
            }}
          >
            <motion.h1 
              className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-black text-2xl sm:text-3xl md:text-[42px] text-center tracking-[0] leading-tight md:leading-[42px] px-4 md:px-0"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {config.title}
            </motion.h1>
          </motion.div>

          <motion.div 
            className="flex flex-col max-w-2xl w-full md:w-[672px] items-center pt-2 md:pt-[7px] pb-0 px-4 md:px-0"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7,
                  ease: "easeOut"
                }
              }
            }}
          >
            <motion.p 
              className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-sm md:text-[17.2px] text-center tracking-[0] leading-relaxed md:leading-[24.5px]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {config.description}
            </motion.p>
          </motion.div>
        </motion.header>

        <motion.div 
          className="flex flex-col lg:flex-row items-start justify-center gap-6 md:gap-7 w-full"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
              }
            }
          }}
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, scale: 0.95 },
              visible: {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              },
              hover: {
                y: -5,
                scale: 1.02,
                transition: {
                  duration: 0.3,
                  ease: "easeInOut"
                }
              }
            }}
            whileHover="hover"
          >
            <Card className="flex flex-col w-full lg:w-[709.33px] items-start p-px rounded-[12.75px] border border-solid border-[#bddaff] bg-[linear-gradient(150deg,rgba(240,253,244,1)_0%,rgba(219,234,254,0.5)_100%)]">
              <CardContent className="flex flex-col items-start gap-5 md:gap-7 pt-6 md:pt-[35px] pb-4 md:pb-[21px] px-4 md:px-[35px] w-full">
                <motion.div 
                  className="flex flex-col w-full gap-3 md:gap-[13.3px]"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex items-start justify-between w-full">
                    <div className="flex flex-col gap-3 md:gap-[13.3px] flex-1">
                      <motion.div 
                        className="flex flex-col items-start w-full"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        <h2 className="text-xl md:text-[26.3px] leading-tight md:leading-[31.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                          {config.container.title}
                        </h2>
                      </motion.div>

                      <motion.div 
                        className="flex flex-col items-start w-full"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-medium text-[#193bb8] text-sm md:text-[15.8px] tracking-[0] leading-relaxed md:leading-[25.6px]">
                          {config.container.description}
                        </p>
                      </motion.div>
                    </div>

                    <motion.div 
                      className="flex items-center justify-center bg-[#daeafe] rounded-full p-2 ml-4 flex-shrink-0"
                      variants={{
                        animate: {
                          scale: [1, 1.1, 1],
                          transition: {
                            duration: 2,
                            ease: "easeInOut",
                            repeat: Infinity,
                            repeatDelay: 3
                          }
                        }
                      }}
                      animate="animate"
                      whileHover={{ 
                        rotate: 10,
                        scale: 1.1,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <Brain className="size-6 md:size-8 text-blue-500" />
                    </motion.div>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex flex-col sm:flex-row items-start justify-center gap-4 md:gap-[21px] w-full"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                        delayChildren: 0.5
                      }
                    }
                  }}
                >
                  <div className="flex flex-col w-full sm:w-[308.16px] items-start gap-3 md:gap-3.5">
                    {config.container.sub_points.slice(0, 2).map((item, index) => {
                         const Icon = featureItems[index].icon
                      return (
                      <motion.div
                        key={index}
                        className="flex items-center gap-3 md:gap-[10.5px] w-full"
                        variants={{
                          hidden: { opacity: 0, x: -30 },
                          visible: {
                            opacity: 1,
                            x: 0,
                            transition: {
                              duration: 0.5,
                              ease: "easeOut"
                            }
                          }
                        }}
                        whileHover={{ x: 5, transition: { duration: 0.2 } }}
                      >
                        <motion.div 
                          className="flex w-7 h-7 items-center justify-center bg-[#daeafe] rounded-[8.75px] flex-shrink-0"
                          whileHover={{ 
                            scale: 1.1,
                            rotate: 5,
                            transition: { duration: 0.2 }
                          }}
                        >
                          <Icon className="size-3.5" />
                        </motion.div>

                        <div className="flex flex-col items-start min-w-0 flex-1">
                          <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm md:text-[13.8px] tracking-[0] leading-[21px] break-words">
                            {item.text}
                          </span>
                        </div>
                      </motion.div>
                      )
})}
                  </div>

                  <div className="flex flex-col w-full sm:w-[308.17px] items-start gap-3 md:gap-3.5">
                    {config.container.sub_points.slice(2, 4).map((item, index) => {
                      const Icon = featureItems[index + 2].icon
                      return(
                      <motion.div
                        key={index}
                        className="flex items-center gap-3 md:gap-[10.5px] w-full"
                        variants={{
                          hidden: { opacity: 0, x: -30 },
                          visible: {
                            opacity: 1,
                            x: 0,
                            transition: {
                              duration: 0.5,
                              ease: "easeOut"
                            }
                          }
                        }}
                        whileHover={{ x: 5, transition: { duration: 0.2 } }}
                      >
                        <motion.div 
                          className="flex w-7 h-7 items-center justify-center bg-[#daeafe] rounded-[8.75px] flex-shrink-0"
                          whileHover={{ 
                            scale: 1.1,
                            rotate: 5,
                            transition: { duration: 0.2 }
                          }}
                        >
                          <Icon className="size-3.5" />
                        </motion.div>

                        <div className="flex flex-col items-start min-w-0 flex-1">
                          <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm tracking-[0] leading-[21px] break-words">
                            {item.text}
                          </span>
                        </div>
                      </motion.div>
                      )
})}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <Link
                    href={config.button.href}
                    target="_blank"
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Button className="w-auto md:w-[125.97px] bg-[#155cfb] rounded-[6.75px] h-[31.5px] flex items-center justify-center gap-2 px-3 md:px-2.5 py-[5px]">
                        <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-[12.3px] text-center tracking-[0] leading-[17.5px]">
                          {config.button.title}
                        </span>
                        <motion.div
                          animate={{ x: [0, 3, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <ArrowRightIcon className="w-3.5 h-3.5 text-white" />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div 
            className="flex flex-col w-full lg:w-[340.65px] items-start gap-4 md:gap-[21px]"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.5
                }
              }
            }}
          >
            {config.sideCards.map((card, index) => {
             const Icon = sideCards[index].icon
             return(
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, x: 50, scale: 0.9 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    y: -3,
                    scale: 1.03,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut"
                    }
                  }
                }}
                whileHover="hover"
              >
                <Card className="flex flex-col items-start p-px w-full bg-white rounded-[12.75px] border border-solid">
                  <CardContent className="flex flex-col w-full p-4 md:p-6">
                    <div className="flex items-start gap-4 w-full">
                      <motion.div
                        className={`flex w-[42px] h-[42px] items-center justify-center ${card.bgColor} rounded-[8.75px] flex-shrink-0`}
                        whileHover={{ 
                          scale: 1.15,
                          rotate: 10,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <Icon className={cn("size-6", card.iconClass)} />
                      </motion.div>

                      <motion.div 
                        className="flex flex-col gap-2 flex-1 min-w-0"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                      >
                        <h3 className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm md:text-[15.8px] tracking-[0] leading-tight md:leading-[24.5px] break-words">
                          {card.title}
                        </h3>
                        <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-xs md:text-[12.3px] tracking-[0] leading-relaxed md:leading-[17.5px] break-words">
                          {card.description}
                        </p>
                      </motion.div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              )})}
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};